/**
 * TransactionLineItems
 *
 * Expandable row component that renders transaction line items.
 * Used in both desktop table sub-rows and mobile card layouts.
 *
 * [T113-c-1] Line Item Expandable Row Design
 * [T146]     Refactored to use the shared <ReceiptItemRow> primitive and
 *            <ItemizedSummaryBar> footer so the receipt-style display is
 *            visually consistent across list / detail-sheet / mobile-sheet.
 *            Public API preserved.
 */
import { useId, useState } from 'react';
import { ChevronDown, Layers } from 'lucide-react';
import { cn } from '@/lib/utils';
import ReceiptItemRow from './ReceiptItemRow';
import ItemizedSummaryBar from './ItemizedSummaryBar';
import type { TransactionItem } from '@/lib/types';

interface TransactionLineItemsProps {
  items: TransactionItem[];
  transactionAmount?: number;
  transactionDiscountTotal?: number;
  /** Controls whether summary repeats Net Total copy. */
  showSummaryNetTotal?: boolean;
  /** Reserved for future density toggling. Not used after T146 refactor. */
  compact?: boolean;
  /** When true, renders the items panel directly without a toggle button.
   *  Used by the desktop table sub-row where the toggle lives in the parent row. */
  hideToggle?: boolean;
  className?: string;
}

export default function TransactionLineItems({
  items,
  transactionAmount,
  transactionDiscountTotal,
  showSummaryNetTotal = true,
  hideToggle = false,
  className,
}: TransactionLineItemsProps) {
  const [expanded, setExpanded] = useState(false);
  const panelId = useId();

  if (!items.length) return null;

  // T146: Single receipt-style panel reused for desktop sub-row + mobile expansion.
  const itemSubtotal = items.reduce(
    (sum, item) => sum + item.amount * (item.quantity ?? 1),
    0,
  );
  const itemDiscountTotal = items.reduce((sum, item) => sum + (item.discountAmount ?? 0), 0);
  const itemsTotal = itemSubtotal - itemDiscountTotal;
  const normalizedTransactionAmount =
    transactionAmount != null ? Math.abs(transactionAmount) : undefined;
  const showSummary = normalizedTransactionAmount != null && normalizedTransactionAmount > 0;

  const panel = (
    <div className="border-l-2 border-primary/25 dark:border-primary/20 pl-2.5">
      <div className="divide-y divide-border/40">
        {items.map((item, idx) => (
          <ReceiptItemRow key={idx} item={item} rowNumber={idx + 1} />
        ))}
      </div>
      {showSummary && (
        <div className="mt-1.5 pt-1.5 border-t border-border/40">
          <ItemizedSummaryBar
            itemizedTotal={itemsTotal}
            transactionTotal={transactionAmount!}
            itemSubtotal={itemSubtotal}
            itemDiscountTotal={itemDiscountTotal}
            transactionDiscountTotal={transactionDiscountTotal}
            showNetTotal={showSummaryNetTotal}
          />
        </div>
      )}
    </div>
  );

  // Table sub-row layout (hideToggle=true) — render directly, no toggle.
  if (hideToggle) {
    return <div className={cn('w-full', className)}>{panel}</div>;
  }

  // Mobile / inline-expansion layout — keep the existing pill-toggle UX
  // for any caller that still relies on it (page-level mobile flow now
  // prefers MobileItemsSheet via T146).
  return (
    <div className={cn('w-full', className)}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setExpanded(!expanded);
        }}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
          'min-h-[44px] md:min-h-[28px] [@media(pointer:coarse)]:min-h-[44px] cursor-pointer select-none',
          'bg-primary/10 text-primary hover:bg-primary/15',
          'border border-primary/20',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
          'transition-colors duration-150',
        )}
        aria-label={`${items.length} line items — click to ${expanded ? 'collapse' : 'expand'}`}
        aria-expanded={expanded}
        aria-controls={panelId}
      >
        <Layers className="h-3 w-3 opacity-70" />
        {items.length} {items.length === 1 ? 'item' : 'items'}
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 transition-transform duration-200',
            expanded && 'rotate-180',
          )}
        />
      </button>

      <div
        id={panelId}
        aria-hidden={!expanded}
        className={cn(
          'grid transition-all duration-200 ease-in-out',
          expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
        )}
      >
        <div className="overflow-hidden min-h-0">
          <div className="mt-1.5">{panel}</div>
        </div>
      </div>
    </div>
  );
}
